import useTreeSelectCtrl from '@/shared/Tree/ctrl/useTreeSelectCtrl.ts'
import { memo, useCallback, useMemo, useState } from 'react'
import { ITreeGroup } from '@/shared/Tree/tree.types.ts'
import useTreeExpandCtrl from '@/shared/Tree/ctrl/useTreeExpandCtrl.ts'
import MenuOptionEdit from '@/shared/Dropdowns/DropdownListItem/DropdownListItemEdit'
import MenuOptionDelete from '@/shared/Dropdowns/DropdownListItem/DropdownListItemDelete'
import DefaultTreeGroup from '@/shared/Tree/ui/DefaultTreeGroup'
import css from '@/pages/Parameters/components/ParameterGroupsTree/ParameterTree.module.css'
import Tree from '@/shared/Tree'
import defaultTreeGroupStyling from '@/shared/Tree/ui/DefaultTreeGroup/defaultTreeGroup.styling.ts'
import CreateEntityButton from '@/shared/Buttons/CreateEntityButton'
import useDashboardGroups from '@/pages/Dashboards/hooks/useDashboardGroups.ts'
import confirmService from '@/services/ConfirmService/confirm.service.ts'
import { IDashboardGroup } from '@/pages/Dashboards/dashboards.types.ts'
import dashboardsApi from '@/pages/Dashboards/dashboards.api.ts'
import dashboardsEvents from '@/pages/Dashboards/events/dashboards.events.ts'
import CreateDashboardGroupModal from '@/pages/Dashboards/components/Modals/CreateDashboardGroupModal'
import UpdateDashboardGroupModal from '@/pages/Dashboards/components/Modals/UpdateDashboardGroupModal'

interface IProps {
  treeSelectCtrl: ReturnType<typeof useTreeSelectCtrl>
}

const DashboardGroupsTree = (props: IProps) => {
  // Модальные окна
  const [createGroupModalOpen, setCreateGroupModalOpen] = useState(false)
  const [initialCreateGroupModalParentId, setInitialCreateGroupModalParentId] = useState<IDashboardGroup['id']>(null)
  const [editingGroupId, setEditingGroupId] = useState<IDashboardGroup['id']>(null)

  const [isOpenExpand, setIsOpenExpand] = useState(false)
  const [clickedOnExpand, setClickedOnExpand] = useState(false)

  const groups = useDashboardGroups().groups
  const handleGroupDelete = useCallback(
    (groupId: IDashboardGroup['id']) => {
      const group = groups.find((g) => g.id === groupId)

      confirmService.requestConfirm({
        buttonType: 'primary',
        description: `Вы действительно хотите удалить группу "${group.name}"?`,
        onConfirm: () =>
          dashboardsApi.deleteGroup(group.id).then((r) => {
            if (r.status === 'success') {
              dashboardsEvents.groupDeleted(r.body.id)
            }
          }),
      })
    },
    [groups]
  )

  const treeGroups: ITreeGroup<IDashboardGroup, number>[] = useMemo(() => {
    if (!groups.length) return []

    return groups.map((g) => ({ id: g.id, parentId: g.parentId, data: g, type: 'folder' }))
  }, [groups])

  const expandCtrl = useTreeExpandCtrl(
    'dashboard-groups-tree',
    isOpenExpand,
    treeGroups,
    [],
    setIsOpenExpand,
    clickedOnExpand,
    setClickedOnExpand
  )

  const renderGroup = (group: ITreeGroup<IDashboardGroup, number>) => {
    const menuContent = (
      <>
        <MenuOptionEdit
          text="Редактировать"
          onClick={() => {
            setEditingGroupId(group.data.id)
          }}
        />
        <MenuOptionDelete text="Удалить" onClick={() => handleGroupDelete(group.data.id)} />
      </>
    )

    return (
      <DefaultTreeGroup
        key={group.id}
        group={group.data}
        title={group.data.name}
        onCreate={() => {
          setCreateGroupModalOpen(true)
          setInitialCreateGroupModalParentId(group.data.id)
        }}
        threeDotsMenuContent={menuContent}
      />
    )
  }

  return (
    <>
      <div className={css.wrapperTree}>
        <div className="flex-lines gap8">
          <span className="text-sm text-description" style={{ marginLeft: '12px' }}>
            Группы
          </span>
          <Tree
            leafs={[]}
            groups={treeGroups}
            renderGroup={renderGroup}
            renderLeaf={null}
            styling={{ groupClassname: defaultTreeGroupStyling.groupClassname }}
            expandCtrl={expandCtrl}
            selectCtrl={props.treeSelectCtrl}
            onClickGroup={(group: Record<string, any>) => props.treeSelectCtrl.handleSelect(group.id)}
          />
        </div>
        <div className={css.btnCreateGroup}>
          <CreateEntityButton
            fullWidth={true}
            type="default"
            onClick={() => setCreateGroupModalOpen(true)}
            text="Создать группу"
          />
        </div>
      </div>
      <CreateDashboardGroupModal
        initialParentId={initialCreateGroupModalParentId}
        open={createGroupModalOpen}
        onClose={() => {
          setInitialCreateGroupModalParentId(null)
          setCreateGroupModalOpen(false)
        }}
      />
      <UpdateDashboardGroupModal
        groupId={editingGroupId}
        open={Boolean(editingGroupId)}
        onClose={() => setEditingGroupId(null)}
      />
    </>
  )
}

export default memo(DashboardGroupsTree)
